import type { StandaloneSlideProps } from '@/components/element/Slider/Slide';
import type { StandaloneSwiperProps } from '@/components/element/Slider/Swiper';
import { useStable } from '@/hooks/useStable';
import type { LabradorComponent, StandaloneComponent, StandaloneComponentProps } from '@/types/component';
import { mergeOptions } from '@/utils/merge';
import { withDebugMetadata } from 'lib/utils';
import { standaloneComponent } from 'modules/dynamic-components';
import { useMemo } from 'react';
import { AffiliateGallery, AffiliateGallerySubcomponentProps } from './AffiliateGallery';

const Swiper = standaloneComponent<StandaloneSwiperProps>('ElementSliderSwiper');
const Slide = standaloneComponent<StandaloneSlideProps>('ElementSliderSlide');
Slide.displayName = 'SwiperSlide'; // Required for `Swiper` to work

export interface AffiliateGalleryProduct {
  id: number;
  link: string;
  image_link: string;
  price: string;
  title: string;
}
interface ExtendedOptions {
  $slide?: StandaloneSlideProps['options'];
  $swiper?: StandaloneSwiperProps['options'];
  $line?: AffiliateGallerySubcomponentProps['Disclaimer']['Line'];
}

export interface StandaloneAffiliateGalleryProps
  extends StandaloneComponentProps<typeof AffiliateGallery, ExtendedOptions> {
  disclaimer?: React.ReactNode;
  title?: string;
  products?: AffiliateGalleryProduct[];
}

export const StandaloneAffiliateGallery: StandaloneComponent<StandaloneAffiliateGalleryProps> = ({
  disclaimer,
  title,
  products,
  options,
  ...props
}) => {
  const { $swiper, $slide, $title, $disclaimer, $line, $price, ...$base } = { ...options };

  const memoSlides = useMemo(
    () =>
      products?.map(({ id, link, image_link, price, title }) => (
        <Slide
          key={id}
          url={link}
          title={
            <>
              <AffiliateGallery.Title {...$title}>{title}</AffiliateGallery.Title>
              <AffiliateGallery.Price {...$price}>{price}</AffiliateGallery.Price>
            </>
          }
          imageSrc={image_link}
          options={mergeOptions({ className: 'cts-impression-item' }, $slide)}
          data-cts-id={link}
          data-cts-label="product"
          data-cts-name={title}
          data-cts-creative="affiliate-carousel"
        />
      )),
    [$price, $slide, $title, products]
  );

  return (
    <div className="cts-impression-group">
      <AffiliateGallery
        data-cts-id={title}
        data-cts-label="affiliate-carousel"
        data-cts-name="affiliate-carousel"
        data-cts-creative="affiliate-carousel"
        {...mergeOptions({ className: 'cts-impression-item' }, $base, props)}
      >
        <AffiliateGallery.Disclaimer.Line {...$line}>
          <AffiliateGallery.Disclaimer {...$disclaimer}>{disclaimer}</AffiliateGallery.Disclaimer>
        </AffiliateGallery.Disclaimer.Line>
        <Swiper content={memoSlides} title={title} options={$swiper} />
      </AffiliateGallery>
    </div>
  );
};

export const standalone = StandaloneAffiliateGallery;

const DynamicStandaloneAffiliateGallery = standaloneComponent('ContentAffiliateGallery') as typeof standalone;

const LabradorAffiliateGallery: LabradorComponent = ({ type, data, meta }) => {
  const {
    active_module,
    product_module_disclaimer,
    native_module_disclaimer,
    product_module_title,
    native_module_title,
    products,
    natives,
  } = useStable(data.gallery);

  if (!products?.length && !natives?.length) {
    return null;
  }

  const isProductModule = active_module === 'product';

  return (
    <DynamicStandaloneAffiliateGallery
      disclaimer={isProductModule ? product_module_disclaimer : native_module_disclaimer}
      title={isProductModule ? product_module_title : native_module_title}
      products={isProductModule ? products : natives}
      data-content-index={meta.contentIndex}
      {...withDebugMetadata({ type, meta })}
    />
  );
};

export default LabradorAffiliateGallery;
